import React from 'react';
import { Button, Checkbox, Loader, useToasts } from 'freemium-ui';
import { yupResolver } from '@hookform/resolvers/yup';
import { History } from 'history';
import { throttle } from 'lodash';
import qs from 'query-string';
import { UseFormReturn, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Prompt, useHistory, useLocation } from 'react-router-dom';
import { v4 as uuid } from 'uuid';
import * as Yup from 'yup';
import {
  ContactListType,
  FieldGroupsType,
  FormServFieldType,
  McrContactType,
} from '../../../../shared/types';
import {
  BACK_END_FIELDS,
  HIDE_CONTACT_FORM_FIELDS,
  TEMPORARY_HIDDEN_FIELDS,
} from '../../../../shared/constants';
import {
  Center,
  FormActions,
  FormContainer,
  FormContent,
  FormField,
  FormHolder,
  FormSection,
  FormSideBar,
} from '../../../../shared/styles';
import { useGetContact, useSaveContact } from '../../../screens/contacts/hooks';
import { useGetContactLists } from '../../../screens/lists/hooks';
import { useBeforeUnload } from '../../../../hooks';
import {
  getContactPayload,
  getInputValue,
  getSchemaFromFormServFields,
} from '../../../utils';
import { fieldGroups } from '../../../constants';
import ContactField from '../../ContactField';

type NewContactFormProps = {
  setIsDirty: (isDirty: boolean) => void;
  toggleDrawer: () => void;
  executeSegment?: () => void;
  fields: FormServFieldType[];
  values?: any;
};

const elementHeight: number[] = [];

let allowScroll = true;

const NewContactForm = (props: NewContactFormProps) => {
  const { t } = useTranslation();
  const {
    toggleDrawer,
    values = {},
    fields,
    executeSegment,
    setIsDirty,
  } = props;
  const [, setToast] = useToasts();
  const routerLocation = useLocation();
  const { push }: History = useHistory();
  const [val, setVal] = React.useState(false);
  // const [required, setRequired] = useState(true);

  const contentRef: React.MutableRefObject<HTMLDivElement | undefined> =
    React.useRef();
  const refs: {
    [key: string]: React.MutableRefObject<HTMLDivElement | undefined>;
  } = {};
  fieldGroups.forEach((fieldGroup) => {
    refs[fieldGroup.id] = React.useRef();
  });

  const getContactCallBack = (isSuccess: boolean, response: McrContactType) => {
    if (!isSuccess || !response) return;
    const { fields: f, ...allValues } = response || {};
    if (response.id) reset({ ...allValues, ...f });
  };

  const {
    data: contactData = {},
    isLoading: isLoadingContact,
    isError: isContactError,
  } = useGetContact(values.id || '', {
    callback: getContactCallBack,
  });

  const {
    data: contactListData = {},
    isLoading: isLoadingContactLists,
    isError: isContactListsError,
  } = useGetContactLists();
  const { contactList: contactLists } = contactListData;

  const editableFields = fields.filter(
    (field: FormServFieldType) =>
      field.visible &&
      ![
        ...TEMPORARY_HIDDEN_FIELDS,
        ...BACK_END_FIELDS,
        ...HIDE_CONTACT_FORM_FIELDS,
      ].includes(field.name),
  );

  const { mutate: saveContact, isLoading: isSavingContact } = useSaveContact({
    setToast,
    toggleDrawer,
    callback: (isSuccess, savedContact) => {
      if (isSuccess && executeSegment) executeSegment();
      if (!isSuccess) {
        if (savedContact?.response?.data === 'Contact is not unique') {
          setToast({
            type: 'error',
            delay: 3000,
            text: `${t('forms.toast.error.contact_exists')}`,
          });
        } else {
          setToast({
            type: 'error',
            delay: 3000,
            text: `${t('forms.toast.error.contact_creation')}`,
          });
        }
      }
    },
  });
  const [selectedFieldGroup, setSelectedFieldGroup] =
    React.useState<FieldGroupsType>(fieldGroups[0]);
  const form = useForm({
    resolver: yupResolver(
      Yup.object(getSchemaFromFormServFields(editableFields)).required(),
    ),
  });
  const {
    handleSubmit,
    reset,
    setValue,
    formState: { isDirty, submitCount, errors, touchedFields },
  } = form;

  useBeforeUnload(isDirty);

  const onSubmit = (data: any) => {
    saveContact({ payload: getContactPayload(data), touchedFields });
  };
  const setFormValue = (fieldId: any, fieldValue: any) => {
    setValue(fieldId, fieldValue, {
      shouldValidate: true,
      shouldDirty: true,
      shouldTouch: true,
    });
  };

  const handleScroll = () => {
    if (!contentRef || !contentRef.current || !allowScroll) return;
    if (!elementHeight || elementHeight.length === 0) setSectionHeight();
    const scrollDistance = contentRef.current.scrollTop;
    let fieldGroupToSet: FieldGroupsType = fieldGroups[0];
    elementHeight.forEach((elemH, index) => {
      if (scrollDistance >= elemH) {
        fieldGroupToSet = fieldGroups[index + 1];
      }
    });
    if (selectedFieldGroup.id !== fieldGroupToSet.id) {
      setSelectedFieldGroup(fieldGroupToSet);
    }
  };

  const setSectionHeight = () => {
    if (!contentRef || !contentRef.current) return;
    fieldGroups.forEach((fieldGroup, index) => {
      const element = refs[fieldGroup.id];
      if (element && element.current) {
        const height = element.current.offsetHeight + 48;
        elementHeight[index] =
          (index > 0 ? elementHeight[index - 1] : 0) + height;
      }
    });
  };

  const handleFieldGroupClick = (fieldGroupId: string, index: number) => {
    allowScroll = allowScroll ? false : allowScroll;
    setTimeout(() => {
      allowScroll = true;
    }, index * 400);
    if (!elementHeight || elementHeight.length === 0) setSectionHeight();
    let scrollDistance = 0;
    if (fieldGroupId && refs && refs[fieldGroupId]) {
      if (index > 0) scrollDistance = elementHeight[index - 1];
    }
    if (contentRef && contentRef.current)
      contentRef.current.scrollTop = scrollDistance;
  };

  React.useEffect(() => {
    if (contactData.id && values.id && contactData.id === values.id) {
      getContactCallBack(true, contactData);
    }
  }, []);

  React.useEffect(() => {
    const fieldsToPopulate: any = {};
    // We need to populate some hidden field values too
    // So need not use editableFields
    fields.forEach((field) => {
      if (field.default_value) {
        fieldsToPopulate[field.name] = getInputValue(
          field.type,
          field.default_value,
        );
      }
    });
    if (!contactData.id) {
      reset({
        ...fieldsToPopulate,
        external_id: uuid(),
      });
    }
  }, [fields]);

  React.useEffect(() => {
    setIsDirty(isDirty);
  }, [isDirty]);

  React.useEffect(() => {
    if (!routerLocation || !routerLocation.search) return;
    const parsedValues = qs.parse(routerLocation.search);
    const { contact_data, ...rest }: { contact_data?: string } = parsedValues;
    if (!contact_data) return;
    const contactData: { [key in string]: any } = contact_data
      ? JSON.parse(contact_data)
      : undefined;
    Object.keys(contactData || {}).forEach((key) => {
      setFormValue(key, contactData[key]);
    });
    if (contact_data || Object.keys(rest).length > 0) {
      const newURL = qs.stringifyUrl({
        url: routerLocation.pathname,
        query: { ...(rest || {}) },
      });
      push(newURL);
    }
  }, []);

  if (isLoadingContact || isLoadingContactLists)
    return (
      <Center>
        <Loader />
      </Center>
    );

  if ((isContactError || isContactListsError) && values.id)
    return <Center>{t('empty_state.message.network_error')}</Center>;

  return (
    <FormHolder>
      <Prompt
        when={isDirty}
        message={(location) => {
          return location.pathname && isDirty
            ? `${t('contacts.new_contact.not_saved')}`
            : true;
        }}
      />
      <FormSideBar>
        <div className="group-heading">{`${
          contactData.id
            ? `${t('contacts.edit_contact')}`
            : `${t('new_contact')}`
        }`}</div>
        <div className="group-items">
          {fieldGroups.map((fieldGroup: FieldGroupsType, index: number) => {
            const groupFields = editableFields.filter(fieldGroup.filter);
            const isError =
              submitCount > 0 &&
              groupFields.some((groupField: FormServFieldType) =>
                Boolean(errors[groupField.name]),
              );
            return (
              <div
                className={`group-item ${isError ? 'error' : ''} ${
                  fieldGroup.id === selectedFieldGroup.id ? 'active' : ''
                }`}
                onClick={() => {
                  setSelectedFieldGroup(fieldGroup);
                  handleFieldGroupClick(fieldGroup.id, index);
                }}
                key={fieldGroup.id}
              >
                {t(fieldGroup.name)}
                {isError ? <span className="error">*</span> : <span />}
              </div>
            );
          })}
        </div>
      </FormSideBar>
      <FormContainer
        noValidate
        onSubmit={handleSubmit(onSubmit, (err) => console.log('ERR', err))}
      >
        <FormContent
          onScroll={throttle(handleScroll, 500)}
          ref={contentRef}
          id="contact-create-form"
        >
          {fieldGroups.map((fieldGroup) => (
            <React.Fragment key={fieldGroup.id}>
              <FilteredSection
                id={fieldGroup.id}
                form={form}
                ref={refs[fieldGroup.id]}
                contactLists={contactLists || []}
                fields={editableFields.filter(fieldGroup.filter)}
                setFormValue={setFormValue}
                name={`${t(fieldGroup.name)}`}
              />
            </React.Fragment>
          ))}
        </FormContent>

        <div className="disclaimer">
          <Checkbox
            checked={val}
            isRequired={true}
            name="disclaimer"
            onChange={() => {
              setVal(!val);
            }}
          >
            <span className="disclaimer-text">
              {t('contacts.new_contact.disclaimer_text')}
            </span>
          </Checkbox>
        </div>

        <FormActions>
          <div />
          <div>
            <Button
              inline
              htmlType="button"
              type="secondary"
              onClick={toggleDrawer}
            >
              {t('cancel')}
            </Button>
            <Button
              inline
              htmlType="submit"
              disabled={!isDirty || isSavingContact || !val}
            >
              {isSavingContact ? `${t('saving')}...` : `${t('save')}`}
            </Button>
          </div>
        </FormActions>
      </FormContainer>
    </FormHolder>
  );
};

const FilteredSection = React.forwardRef(
  (
    props: {
      fields: FormServFieldType[];
      contactLists: ContactListType[];
      id: string;
      name: string;
      form: UseFormReturn;
      setFormValue: (fieldId: any, fieldValue: any) => void;
    },
    ref,
  ) => {
    const { t } = useTranslation();
    const {
      id,
      fields = [],
      contactLists = [],
      name,
      form,
      setFormValue,
    } = props;
    return (
      <FormSection ref={ref} id={id}>
        <FormField>
          <div className="heading">{name || ''}</div>
        </FormField>
        {fields && fields.length > 0 ? (
          fields.map((field: FormServFieldType) => (
            <ContactField
              key={field.id}
              setFormValue={setFormValue}
              field={field}
              form={form}
              contactLists={contactLists}
            />
          ))
        ) : (
          <FormField>
            <div className="sub-heading">
              {t('contacts.new_contact.no_custom_fields')}
            </div>
          </FormField>
        )}
      </FormSection>
    );
  },
);

FilteredSection.displayName = 'FilteredSection';

export default NewContactForm;
